import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useWallet } from '../../contexts/WalletContext';
import { useToast } from '../../contexts/ToastContext';

const QuickTradeButton = ({ type, percentage, token, onTradeComplete }) => {
  const { tokens, isAuthenticated } = useAuth();
  const { activeWallet, wallets } = useWallet();
  const { showSuccess, showError, showConfirmation } = useToast();

  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [estimatedAmount, setEstimatedAmount] = useState(null);
  const [walletBalance, setWalletBalance] = useState({ sol: 0, token: 0 });
  const [lastTradeTime, setLastTradeTime] = useState(0);
  const [tradeCount, setTradeCount] = useState(0);
  const [cooldownEnd, setCooldownEnd] = useState(0);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumpbackend.urion.live/api';

  // Rate limiting constants
  const RATE_LIMIT_WINDOW = 60000; // 1 minute
  const MAX_TRADES_PER_MINUTE = 10;
  const MIN_TRADE_INTERVAL = 2000; // 2 seconds between trades
  const COOLDOWN_DURATION = 30000; // 30 seconds cooldown after hitting rate limit

  // Get platform wallet for trading
  const platformWallet = wallets.find(w => w.wallet_type === 'platform') || activeWallet;

  // Rate limiting check
  const checkRateLimit = () => {
    const now = Date.now();
    
    // Check if in cooldown period
    if (now < cooldownEnd) {
      const remainingCooldown = Math.ceil((cooldownEnd - now) / 1000);
      return {
        allowed: false,
        reason: `Cooldown active. Wait ${remainingCooldown}s before trading.`
      };
    }
    
    // Check minimum interval between trades
    if (now - lastTradeTime < MIN_TRADE_INTERVAL) {
      const remainingInterval = Math.ceil((MIN_TRADE_INTERVAL - (now - lastTradeTime)) / 1000);
      return {
        allowed: false,
        reason: `Please wait ${remainingInterval}s between trades.`
      };
    }
    
    // Reset trade count if outside the rate limit window
    if (now - lastTradeTime > RATE_LIMIT_WINDOW) {
      setTradeCount(0);
    }
    
    // Check if exceeded max trades per minute
    if (tradeCount >= MAX_TRADES_PER_MINUTE) {
      setCooldownEnd(now + COOLDOWN_DURATION);
      return {
        allowed: false,
        reason: `Rate limit exceeded. Cooldown for 30 seconds.`
      };
    }
    
    return { allowed: true };
  };

  // Authenticated request helper
  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('Please log in to trade');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // Fetch wallet balance
  const fetchBalance = async () => {
    if (!platformWallet || !isAuthenticated) return;

    try {
      // Get SOL balance
      const response = await makeAuthenticatedRequest(`/wallets/${platformWallet.id}/balance`);
      if (response.success) {
        const solBalance = response.data.balance.sol || 0;
        
        // For token balance, we need to check if the wallet holds this specific token
        // This would require a more detailed balance endpoint or token-specific balance check
        // For now, we'll simulate token balance based on recent trades
        let tokenBalance = 0;
        
        try {
          // Try to get recent trades to estimate token balance
          const tradesResponse = await makeAuthenticatedRequest(
            `/trading/history?wallet_id=${platformWallet.id}&token_mint=${token.address}&limit=10`
          );
          
          if (tradesResponse.success && tradesResponse.data.trades.length > 0) {
            // Calculate approximate token balance from recent trades
            // This is a simplified approach - in production, you'd want proper token balance tracking
            const recentTrades = tradesResponse.data.trades;
            let estimatedBalance = 0;
            
            recentTrades.forEach(trade => {
              if (trade.trade_type === 'buy' && trade.status === 'completed') {
                estimatedBalance += parseFloat(trade.output_amount || 0);
              } else if (trade.trade_type === 'sell' && trade.status === 'completed') {
                estimatedBalance -= parseFloat(trade.input_amount || 0);
              }
            });
            
            tokenBalance = Math.max(0, estimatedBalance);
          }
        } catch (tokenError) {
          console.warn('Could not fetch token balance:', tokenError.message);
          // Default to 0 if we can't determine token balance
          tokenBalance = 0;
        }
        
        setWalletBalance({ sol: solBalance, token: tokenBalance });
      }
    } catch (error) {
      console.warn('Could not fetch wallet balance:', error.message);
    }
  };

  // Calculate estimated trade amount
  const calculateEstimatedAmount = () => {
    if (!walletBalance) return null;

    if (type === 'buy') {
      // Calculate SOL amount based on percentage of available balance
      const maxAmount = Math.max(0, walletBalance.sol - 0.02); // Reserve 0.02 SOL for fees
      const amount = (maxAmount * percentage / 100);
      return {
        solAmount: amount,
        display: `${amount.toFixed(4)} SOL`,
        canTrade: amount >= 0.005 // Minimum trade amount
      };
    } else {
      // For sell, we need token balance (placeholder for now)
      const tokenAmount = (walletBalance.token * percentage / 100);
      return {
        tokenAmount: tokenAmount,
        display: `${tokenAmount.toFixed(6)} ${token.symbol}`,
        canTrade: tokenAmount > 0
      };
    }
  };

  // Execute trade
  const executeTrade = async () => {
    if (!platformWallet || !isAuthenticated) {
      showError('Please connect a platform wallet to trade');
      return;
    }

    // Check rate limiting
    const rateLimitCheck = checkRateLimit();
    if (!rateLimitCheck.allowed) {
      showError(rateLimitCheck.reason);
      return;
    }

    const estimate = calculateEstimatedAmount();
    if (!estimate || !estimate.canTrade) {
      showError('Insufficient balance for this trade');
      return;
    }

    setLoading(true);
    const tradeStartTime = Date.now();

    try {
      let result;
      
      if (type === 'buy') {
        // Execute buy order
        result = await makeAuthenticatedRequest('/trading/manual-buy', {
          method: 'POST',
          body: JSON.stringify({
            tokenMint: token.address,
            walletId: platformWallet.id,
            solAmount: estimate.solAmount
          })
        });
      } else {
        // Execute sell order
        result = await makeAuthenticatedRequest('/trading/manual-sell', {
          method: 'POST',
          body: JSON.stringify({
            tokenMint: token.address,
            walletId: platformWallet.id,
            tokenAmount: estimate.tokenAmount
          })
        });
      }

      if (result.success) {
        // Update rate limiting counters
        setLastTradeTime(tradeStartTime);
        setTradeCount(prev => prev + 1);
        
        showSuccess(`${type === 'buy' ? 'Buy' : 'Sell'} order completed successfully!`);
        
        // Refresh balance and notify parent
        await fetchBalance();
        if (onTradeComplete) {
          onTradeComplete(result.data);
        }
      } else {
        throw new Error(result.error?.message || 'Trade failed');
      }
    } catch (error) {
      showError(`Trade failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle button click with confirmation
  const handleClick = () => {
    const estimate = calculateEstimatedAmount();
    if (!estimate || !estimate.canTrade) return;

    const action = type === 'buy' ? 'Buy' : 'Sell';
    const message = `${action} ${estimate.display} of ${token.symbol}?`;

    showConfirmation(message, executeTrade);
  };

  // Update estimated amount and disabled state
  useEffect(() => {
    const estimate = calculateEstimatedAmount();
    const rateLimitCheck = checkRateLimit();
    
    setEstimatedAmount(estimate);
    setDisabled(
      !estimate ||
      !estimate.canTrade ||
      !isAuthenticated ||
      !platformWallet ||
      !rateLimitCheck.allowed ||
      loading
    );
  }, [walletBalance, percentage, type, isAuthenticated, platformWallet, lastTradeTime, tradeCount, cooldownEnd, loading]);

  // Fetch balance on mount and when wallet changes
  useEffect(() => {
    fetchBalance();
  }, [platformWallet, isAuthenticated]);

  // Get button label
  const getButtonLabel = () => {
    if (loading) return '⏳';
    
    if (percentage === 100) {
      return type === 'buy' ? 'MAX' : 'ALL';
    }
    
    return `${percentage}%`;
  };

  // Get button class
  const getButtonClass = () => {
    let baseClass = 'quick-trade-btn';
    baseClass += ` quick-trade-btn-${type}`;
    if (disabled) baseClass += ' aaactive';
    if (loading) baseClass += ' loading';
    return baseClass;
  };

  // Get tooltip text
  const getTooltipText = () => {
    if (!isAuthenticated) return 'Please log in to trade';
    if (!platformWallet) return 'Please connect a platform wallet';
    if (!estimatedAmount) return 'Loading...';
    if (!estimatedAmount.canTrade) return 'Insufficient balance';
    
    // Check rate limiting for tooltip
    const rateLimitCheck = checkRateLimit();
    if (!rateLimitCheck.allowed) return rateLimitCheck.reason;
    
    const action = type === 'buy' ? 'Buy' : 'Sell';
    return `${action} ${estimatedAmount.display}`;
  };

  return (
    <button
      className={getButtonClass()}
      onClick={handleClick}
      disabled={disabled || loading}
      title={getTooltipText()}
    >
      {getButtonLabel()}
    </button>
  );
};

export default QuickTradeButton;